// frontend/packages/shared/config/src/azureB2C.js

/*
 * Updated Azure B2C configuration helper.
 *
 * This version treats either a legacy `signUpSignIn` policy or a
 * `customerSignUpSignIn` policy as a trigger for B2C mode. When a
 * customer policy is defined, it takes precedence over the legacy
 * policy. Otherwise the legacy policy is used, and if neither is
 * present the helper falls back to CIAM (tenant‑level) mode.
 */

const parseCommaSeparatedList = (value) => {
  if (!value) return [];
  return value
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
};

const normalizeTenantDomain = (tenantName = '') => {
  if (!tenantName) return '';
  return tenantName.includes('.') ? tenantName : `${tenantName}.onmicrosoft.com`;
};

const trimTrailingSlash = (value = '') => value.replace(/\/+$/g, '');

const buildAuthorityUrl = (baseUrl, tenantDomain, policyName) => {
  if (!baseUrl || !tenantDomain || !policyName) {
    return '';
  }
  const trimmedBase = trimTrailingSlash(baseUrl);
  return `${trimmedBase}/${tenantDomain}/${policyName}`;
};

const tenantName = process.env.REACT_APP_AZURE_B2C_TENANT_NAME || '';
const tenantId = process.env.REACT_APP_AZURE_B2C_TENANT_ID || '';
const authorityBaseUrl =
  process.env.REACT_APP_AZURE_B2C_AUTHORITY_BASE_URL || '';
const normalizedTenantDomain = normalizeTenantDomain(tenantName);
const trimmedAuthorityBaseUrl = trimTrailingSlash(authorityBaseUrl);

const policies = {
  signUpSignIn: process.env.REACT_APP_AZURE_B2C_SIGNUP_SIGNIN_POLICY || '',
  editProfile: process.env.REACT_APP_AZURE_B2C_EDIT_PROFILE_POLICY || '',
  customerSignUpSignIn:
    process.env.REACT_APP_AZURE_B2C_CUSTOMER_SIGNUP_SIGNIN_POLICY || '',
};

const authorities = {
  signUpSignIn: buildAuthorityUrl(
    authorityBaseUrl,
    normalizedTenantDomain,
    policies.signUpSignIn,
  ),
  editProfile: buildAuthorityUrl(
    authorityBaseUrl,
    normalizedTenantDomain,
    policies.editProfile,
  ),
  customerSignUpSignIn: buildAuthorityUrl(
    authorityBaseUrl,
    normalizedTenantDomain,
    policies.customerSignUpSignIn,
  ),
};

const isCiamHost = /\.ciamlogin\.com(?:$|\/)/i.test(trimmedAuthorityBaseUrl);
const hasB2CPolicies = Boolean(
  policies.signUpSignIn || policies.customerSignUpSignIn,
);
const hasLegacyPolicies = hasB2CPolicies && !isCiamHost;

const resolveCiamBaseUrl = () => {
  if (trimmedAuthorityBaseUrl) {
    return trimmedAuthorityBaseUrl;
  }
  if (!tenantName) {
    return '';
  }
  const normalized = normalizeTenantDomain(tenantName);
  if (!normalized) {
    return '';
  }
  const host = normalized.replace(/\.onmicrosoft\.com$/i, '');
  if (!host) {
    return '';
  }
  return `https://${host}.ciamlogin.com`;
};

const ciamAuthority = (() => {
  if (!tenantId) {
    return '';
  }
  const base = resolveCiamBaseUrl();
  if (!base) {
    return '';
  }
  return `${base}/${tenantId}`;
})();

const defaultAuthority = hasLegacyPolicies
  ? policies.customerSignUpSignIn
    ? authorities.customerSignUpSignIn
    : authorities.signUpSignIn
  : ciamAuthority;

const azureB2CConfig = {
  tenantName,
  tenantId,
  authorityBaseUrl: trimmedAuthorityBaseUrl,
  rawAuthorityBaseUrl: authorityBaseUrl,
  policies,
  authorities,
  tenantDomain: normalizedTenantDomain,
  hasLegacyPolicies,
  mode: hasLegacyPolicies ? 'b2c' : 'ciam',
  defaultAuthority,
  frontendClientId:
    process.env.REACT_APP_AZURE_B2C_FRONTEND_CLIENT_ID ||
    process.env.REACT_APP_MSAL_CLIENT_ID ||
    '',
  backendClientId: process.env.REACT_APP_AZURE_B2C_BACKEND_CLIENT_ID || '',
  scopes: parseCommaSeparatedList(process.env.REACT_APP_AZURE_B2C_SCOPES),
  redirectUri: process.env.REACT_APP_AZURE_B2C_REDIRECT_URI || '',
  postLogoutRedirectUri:
    process.env.REACT_APP_AZURE_B2C_POST_LOGOUT_REDIRECT_URI || '',
  jwksUri: process.env.REACT_APP_AZURE_B2C_JWKS_URI || '',
  issuer: process.env.REACT_APP_AZURE_B2C_ISSUER || '',
};

export default azureB2CConfig;
export {
  azureB2CConfig as azureB2C,
  parseCommaSeparatedList,
  normalizeTenantDomain,
  buildAuthorityUrl,
};